import { useEffect, useState, useCallback } from "react";

const TRIP_KEY = "vantastika_pending_trip";
const WISHLIST_KEY = "vantastika_pending_wishlist";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface PendingWishlistItem {
  name: string;
  place_type?: string;
  country?: string;
  region?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  image_url?: string;
  description?: string;
  google_place_id?: string;
  savedAt: number;
}

export interface GuestSavesState {
  tripCount: number;
  wishlistCount: number;
  totalCount: number;
  oldestSavedAt: number | null;
  expiresAt: number | null;
  msUntilExpiry: number | null;
  wishlistItems: PendingWishlistItem[];
  hasPendingTrip: boolean;
  pendingTripTitle: string | null;
}

function readWishlist(): PendingWishlistItem[] {
  try {
    const raw = sessionStorage.getItem(WISHLIST_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as PendingWishlistItem[];
    if (!Array.isArray(arr)) return [];
    const now = Date.now();
    const fresh = arr.filter((i) => i?.savedAt && now - i.savedAt < MAX_AGE_MS);
    if (fresh.length !== arr.length) {
      sessionStorage.setItem(WISHLIST_KEY, JSON.stringify(fresh));
    }
    return fresh;
  } catch {
    return [];
  }
}

function readTrip(): { hasTrip: boolean; title: string | null; savedAt: number | null } {
  try {
    const raw = sessionStorage.getItem(TRIP_KEY);
    if (!raw) return { hasTrip: false, title: null, savedAt: null };
    const parsed = JSON.parse(raw);
    const savedAt = parsed?.savedAt as number | undefined;
    if (!savedAt || Date.now() - savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(TRIP_KEY);
      return { hasTrip: false, title: null, savedAt: null };
    }
    return { hasTrip: true, title: parsed?.tripData?.title ?? null, savedAt };
  } catch {
    return { hasTrip: false, title: null, savedAt: null };
  }
}

function compute(): GuestSavesState {
  const wishlist = readWishlist();
  const trip = readTrip();
  const tripCount = trip.hasTrip ? 1 : 0;
  const wishlistCount = wishlist.length;
  const totalCount = tripCount + wishlistCount;
  const allSaved = [
    ...wishlist.map((w) => w.savedAt),
    ...(trip.savedAt ? [trip.savedAt] : []),
  ];
  const oldest = allSaved.length ? Math.min(...allSaved) : null;
  const expires = oldest ? oldest + MAX_AGE_MS : null;
  const ms = expires ? Math.max(0, expires - Date.now()) : null;
  return {
    tripCount,
    wishlistCount,
    totalCount,
    oldestSavedAt: oldest,
    expiresAt: expires,
    msUntilExpiry: ms,
    wishlistItems: wishlist,
    hasPendingTrip: trip.hasTrip,
    pendingTripTitle: trip.title,
  };
}

const EVENT_NAME = "vantastika:guest-saves-changed";

export function notifyGuestSavesChanged() {
  window.dispatchEvent(new Event(EVENT_NAME));
}

export function addGuestWishlistItem(item: Omit<PendingWishlistItem, "savedAt">) {
  try {
    const current = readWishlist();
    // Dedup by name + coords
    const exists = current.some(
      (i) =>
        i.name.toLowerCase() === item.name.toLowerCase() &&
        Math.abs((i.latitude ?? 0) - (item.latitude ?? 0)) < 0.001 &&
        Math.abs((i.longitude ?? 0) - (item.longitude ?? 0)) < 0.001
    );
    if (exists) return false;
    const next = [...current, { ...item, savedAt: Date.now() }];
    sessionStorage.setItem(WISHLIST_KEY, JSON.stringify(next));
    notifyGuestSavesChanged();
    return true;
  } catch {
    return false;
  }
}

export function removeGuestWishlistItem(name: string, lat?: number, lng?: number) {
  try {
    const current = readWishlist();
    const next = current.filter(
      (i) =>
        !(
          i.name.toLowerCase() === name.toLowerCase() &&
          Math.abs((i.latitude ?? 0) - (lat ?? 0)) < 0.001 &&
          Math.abs((i.longitude ?? 0) - (lng ?? 0)) < 0.001
        )
    );
    sessionStorage.setItem(WISHLIST_KEY, JSON.stringify(next));
    notifyGuestSavesChanged();
  } catch {
    /* noop */
  }
}

export function clearGuestSaves() {
  try {
    sessionStorage.removeItem(WISHLIST_KEY);
    sessionStorage.removeItem(TRIP_KEY);
    notifyGuestSavesChanged();
  } catch {
    /* noop */
  }
}

export function useGuestSaves(): GuestSavesState {
  const [state, setState] = useState<GuestSavesState>(() => compute());

  const refresh = useCallback(() => setState(compute()), []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener(EVENT_NAME, onChange);
    window.addEventListener("storage", onChange);
    window.addEventListener("focus", onChange);
    // Live countdown — re-eval every 30s
    const t = window.setInterval(refresh, 30_000);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener("storage", onChange);
      window.removeEventListener("focus", onChange);
      window.clearInterval(t);
    };
  }, [refresh]);

  return state;
}
